import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiPieChart, FiAward } = FiIcons;

const TransparencySection = () => {
  const certificates = [
    { name: 'Section 8 Company', icon: FiShield, description: 'Registered non-profit organization' },
    { name: '12A Registration', icon: FiAward, description: 'Income tax exemption certified' },
    { name: '80G Certification', icon: FiPieChart, description: 'Tax deduction benefits for donors' }
  ];

  const fundAllocation = [
    { category: 'Direct Programs', percentage: 75, color: 'bg-orange-500' },
    { category: 'Administration', percentage: 15, color: 'bg-blue-500' },
    { category: 'Fundraising', percentage: 10, color: 'bg-green-500' }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Transparency & Trust</h2>
          <p className="text-xl text-gray-600">
            Your trust is our foundation. See exactly how your donations create impact.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Certificates */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Our Certifications</h3>
            <div className="space-y-6">
              {certificates.map((cert, index) => (
                <motion.div
                  key={cert.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={cert.icon} className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{cert.name}</h4>
                    <p className="text-gray-600 text-sm">{cert.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Fund Allocation */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Where Your Money Goes</h3>
            <div className="space-y-6">
              {fundAllocation.map((item, index) => (
                <motion.div
                  key={item.category}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="space-y-2"
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-900">{item.category}</span>
                    <span className="font-bold text-gray-900">{item.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${item.percentage}%` }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                      viewport={{ once: true }}
                      className={`h-3 rounded-full ${item.color}`}
                    ></motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
              className="mt-8 p-6 bg-orange-50 rounded-lg"
            >
              <h4 className="font-semibold text-gray-900 mb-2">Financial Transparency</h4>
              <p className="text-gray-700 text-sm mb-4">
                We publish detailed annual reports and quarterly updates on fund utilization.
              </p>
              <button className="text-orange-600 font-medium hover:text-orange-700 transition-colors duration-200">
                Download Latest Report →
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TransparencySection;